// Backend Users translations for React Admin resource
export const backendUsersResource = {
  name: 'Admin Users',
  fields: {
    id: 'ID',
    username: 'Username',
    email: 'Email',
    roleId: 'Role',
    isActive: 'Active',
    totpEnabled: '2FA Enabled',
    lastLoginAt: 'Last Login',
    failedLoginAttempts: 'Failed Attempts',
    lockedUntil: 'Locked Until',
    language: 'Language',
    vendor: 'Merchant',
    vendorId: 'Merchant',
    createdAt: 'Created At',
    updatedAt: 'Updated At',
    password: 'Password',
    requirePasswordChange: 'Require Password Change on First Login',
  },
  noVendor: 'No merchant',
};

// Custom backend users translations for components
export const backendUsers = {
  list: {
    title: 'Backend Users',
    filters: {
      search: 'Search by username or email',
      activeOnly: 'Show Active Only',
      role: 'Role',
      language: 'Language',
    },
    empty: {
      title: 'No users found',
      subtitle: 'Create your first backend user to manage the system.',
    },
  },
  edit: {
    title: 'Edit User',
    basicInfo: 'Basic Information',
    securitySettings: 'Security Settings',
    accountStatus: 'Account Status',
    deleteAccount: 'Delete Account',
    deleteAccountDescription: 'Permanently delete this user account and all associated data',
    cannotDeleteOwnAccount: 'You cannot delete your own account',
    activeAccount: 'Active Account',
    twoFactorAuth: 'Two-Factor Authentication',
    twoFactorHelpText: 'Users can enable/disable 2FA from their profile',
    admin2faDisableHelp: 'You can disable 2FA for this user if they have lost access to their authenticator',
    admin2faEnableHelp: 'Users must enable 2FA themselves from their profile - this ensures proper authenticator setup',
    admin2faDisableWarning: 'You are about to disable two-factor authentication for this user.',
    admin2faDisableMessage: 'This will remove 2FA protection from %{username} (%{email}). They will need to set it up again if they want to re-enable it.',
    admin2faDisableReason: 'This action should only be performed if the user has lost access to their authenticator device.',
    admin2faOwnAccountWarning: 'You cannot modify your own 2FA settings here. Use your profile page instead.',
    admin2faUserNoTwoFactor: 'This user does not have 2FA enabled. Users must enable 2FA themselves from their profile.',
    passwordManagement: 'Password Management',
    passwordResetHelp: 'To reset the user\'s password, use the "Send Password Reset Email" action or manually provide a temporary password.',
    lastLogin: 'Last Login',
    failedLoginAttempts: 'Failed Login Attempts',
    accountLockedUntil: 'Account Locked Until',
    accountUnlockHelp: 'Account will be automatically unlocked after this time',
    deleteConfirmTitle: 'Delete User',
    deleteConfirmWarning: 'Warning: This action cannot be undone.',
    deleteConfirmMessage: 'You are about to delete user',
    deleteConfirmInfo: 'All associated data will be permanently removed.',
    deleteConfirmPrompt: 'To confirm deletion, please type the username',
    deleteConfirmPromptBelow: 'below:',
    deleteConfirmTypePlaceholder: 'Type "%{username}" to confirm',
    usernameDoesNotMatch: 'Username does not match',
    deleteButton: 'Delete User',
    backToList: 'Back to List',
    vendorAssignment: 'Merchant Assignment',
    vendorHelperText: 'Change merchant assignment or leave empty to remove merchant access.',
    vendorAssignmentInfo: 'Changing merchant assignment will affect what data this user can access. Removing merchant assignment makes them a system administrator.',
  },
  create: {
    title: 'Create User',
    subtitle: 'Add a new backend administrator',
    infoMessage: 'Creating a new backend user with administrative access. The user will receive an email with login instructions.',
    basicInfo: 'Basic Information',
    securitySettings: 'Security Settings',
    usernameHelperText: 'Unique username for login',
    emailHelperText: 'User will receive login instructions at this email',
    roleHelperText: 'Determines user permissions',
    temporaryPassword: 'Temporary Password',
    passwordHelperText: 'Must contain at least 8 characters, including uppercase, lowercase, number, and special character',
    generatePassword: 'Generate',
    passwordGenerated: 'Password generated',
    passwordGeneratedCopied: 'Password generated and copied to clipboard',
    passwordValidation: {
      minLength: 'Must contain at least one uppercase letter',
      uppercase: 'Must contain at least one uppercase letter',
      lowercase: 'Must contain at least one lowercase letter',
      number: 'Must contain at least one number',
      special: 'Must contain at least one special character',
    },
    requirePasswordChange: 'Require Password Change on First Login',
    twoFactorInfo: 'Two-Factor Authentication',
    twoFactorHelperText: 'Users can enable two-factor authentication from their profile after logging in. This provides an additional layer of security for their account.',
    vendorAssignment: 'Merchant Assignment (Optional)',
    vendorHelperText: 'Assign this user to a merchant organization. Leave empty for system administrators.',
    vendorAssignmentInfo: 'Assigning a user to a merchant allows them to manage RFID cards and view data for that merchant only. System administrators should not be assigned to merchants.',
  },
  notifications: {
    deleteSuccess: 'User deleted successfully',
    deleteError: 'Error deleting user',
    createSuccess: 'User created successfully',
    updateSuccess: 'User updated successfully',
    error: {
      create: 'Failed to create user',
      update: 'Failed to update user',
      delete: 'Failed to delete user',
    },
  },
  status: {
    active: 'Active',
    inactive: 'Inactive',
  },
  actions: {
    create: 'Create User',
    save: 'Save User',
    delete: 'Delete User',
    cancel: 'Cancel',
    sendPasswordReset: 'Send Password Reset Email',
    disable2FA: 'Disable 2FA',
    unlockAccount: 'Unlock Account',
    actions: 'Actions',
    resetPassword: 'Reset password',
  },
  twoFactorAuth: {
    confirmDisable: 'Are you sure you want to disable 2FA for this user?',
    disabledSuccess: '2FA disabled successfully',
    disabledError: 'Failed to disable 2FA',
  },
  passwordReset: {
    sectionTitle: 'Password',
    adminHelp:
      "Reset this user's password. You can either generate a strong random one or set a specific password yourself.",
    ownAccountHelp:
      'Use the change-password section in your profile to change your own password.',
    ownAccountTooltip:
      'Use the change-password section in My Profile to change your own password.',
    confirmTitle: "Reset this user's password?",
    confirmWarningTitle: 'This action will:',
    confirmBullet1: "Replace the user's password",
    confirmBullet2: 'Sign the user out of all active sessions',
    confirmBullet3: 'Clear any account lock from failed login attempts',
    confirmBodyRandom:
      'You will see the temporary password ONCE. Copy it and deliver it to the user through a secure channel.',
    confirmBodyCustom:
      'The user will have to log in with this password. Make sure to share it through a secure channel.',
    modeLabel: 'New password',
    modeRandom: 'Generate a strong random password (shown to you once)',
    modeCustom: 'Set a specific password',
    passwordLabel: 'New password',
    confirmLabel: 'Confirm new password',
    passwordHint: 'Min 8 characters with uppercase, lowercase, digit, and at least one special character',
    passwordTooShort: 'Must be at least 8 characters',
    passwordWeak: 'Must include uppercase, lowercase, digit, and at least one special character',
    confirmMismatch: 'Passwords do not match',
    cancel: 'Cancel',
    confirm: 'Reset password',
    resultTitle: 'Temporary password',
    resultTitleCustom: 'Password reset',
    resultWarning:
      'This password is shown only once. Copy it now and deliver it to the user through a secure channel.',
    customSuccess:
      'Password set. The user has been signed out and must use the new password to log in.',
    done: 'Done',
    failed: 'Failed to reset password',
    copy: 'Copy',
    copied: 'Copied!',
  },
};